import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { complaintsService } from '../../services/complaintsService';
import { snackActions } from '../../utils/snackbarUtils';

interface ComplaintFormData {
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'rejected' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  resolution?: string;
}

const statusOptions = [
  { value: 'open', label: 'Ouvert' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'resolved', label: 'Résolu' },
  { value: 'rejected', label: 'Rejeté' },
  { value: 'on_hold', label: 'En attente' },
];

const priorityOptions = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high', label: 'Haute' },
  { value: 'critical', label: 'Critique' },
];

const ComplaintEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ComplaintFormData>({
    subject: '',
    description: '',
    status: 'open',
    priority: 'medium',
    resolution: '',
  });

  const fetchComplaint = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const complaint = await complaintsService.getComplaintById(parseInt(id));
      setFormData({
        subject: complaint.subject,
        description: complaint.description,
        status: complaint.status,
        priority: complaint.priority,
        resolution: complaint.resolution || '',
      });
    } catch (error) {
      snackActions.error('Erreur lors du chargement de la réclamation');
      console.error('Error fetching complaint:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchComplaint();
  }, [fetchComplaint]);

  const handleBack = () => {
    navigate(`/reclamations/${id}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setSaving(true);
      
      // Récupérer la réclamation originale pour comparer
      const original = await complaintsService.getComplaintById(parseInt(id));
      
      // Détecter les champs qui ont changé
      const changes: Partial<ComplaintFormData> = {};
      if (formData.subject !== original.subject) changes.subject = formData.subject;
      if (formData.description !== original.description) changes.description = formData.description;
      if (formData.status !== original.status) changes.status = formData.status;
      if (formData.priority !== original.priority) changes.priority = formData.priority;
      if (formData.resolution !== (original.resolution || '')) changes.resolution = formData.resolution;
      
      // Si aucun changement, ne rien faire
      if (Object.keys(changes).length === 0) {
        snackActions.info('Aucune modification détectée');
        navigate(`/reclamations/${id}`);
        return;
      }
      
      // Envoyer uniquement les champs modifiés
      await complaintsService.updateComplaint(parseInt(id), changes);
      snackActions.success('Réclamation mise à jour avec succès');
      navigate(`/reclamations/${id}`);
    } catch (error) {
      snackActions.error('Erreur lors de la mise à jour de la réclamation');
      console.error('Error updating complaint:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement de la réclamation...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Modifier la Réclamation
          </Typography>
        </Box>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sujet"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="Statut"
                    name="status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Priorité</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleSelectChange}
                    label="Priorité"
                    name="priority"
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {(formData.status === 'resolved' || formData.status === 'rejected') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Résolution"
                    name="resolution"
                    value={formData.resolution}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Décrivez la résolution ou la raison du rejet..."
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ComplaintEdit;